import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, MapPin, Users, Check, X, Phone } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Vendor / Venue Dashboard card — paid inquiries waiting for the vendor to
 * confirm availability. Accepting reveals the customer phone + locks the date
 * on the vendor's calendar. Declining auto-refunds any paid advance to the
 * customer's wallet and still marks the calendar busy (same business rule as
 * the pro confirmation flow).
 */
const VendorPendingInquiriesCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/vendor/pending-inquiries`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load inquiries');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleAvailable = async (o) => {
    if (busy) return;
    setBusy(o.slot_id);
    try {
      await axios.post(`${BACKEND_URL}/api/vendor/inquiries/${o.booking_id}/${o.slot_id}/available`, {},
        { withCredentials: true });
      toast.success('Marked available · customer phone revealed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setBusy(null);
  };
  const handleUnavailable = async (o) => {
    if (busy) return;
    const reason = window.prompt('Reason for declining? (optional)\n\nThe customer will get a wallet refund and your calendar will be marked busy on this date.');
    if (reason === null) return;
    setBusy(o.slot_id);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/vendor/inquiries/${o.booking_id}/${o.slot_id}/unavailable`,
        { reason }, { withCredentials: true });
      if (data?.credited_to_wallet > 0) {
        toast.success(`Declined · ₹${data.credited_to_wallet} refunded to customer wallet`);
      } else {
        toast.success('Declined · calendar blocked');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Decline failed');
    }
    setBusy(null);
  };

  if (loading) {
    return (
      <Card data-testid="vendor-inq-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Checking inquiries…</CardContent>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-inq-empty">
        <CardContent className="py-6 text-sm text-text-secondary text-center">
          No pending inquiries. Customer orders will appear here when placed.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-pending-inquiries">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-2xl">
          Pending Customer Inquiries ({items.length})
        </CardTitle>
        <CardDescription>
          Confirm availability to reveal your phone to the customer and theirs to you. Declining auto-refunds any advance they paid.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-3">
        {items.map((o) => (
          <div key={o.slot_id} className="border-2 border-[#E8E1D5] rounded p-3 bg-sand-50/40"
            data-testid={`vendor-inq-${o.slot_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="font-bold text-sm">{o.name}</div>
                <div className="text-xs text-text-secondary">Booking · {o.display_id || o.booking_id.slice(-6)} · {o.customer_name || 'Customer'}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-heading text-lg font-bold text-brand-primary">₹{Number(o.amount).toLocaleString('en-IN')}</div>
                {o.advance_paid > 0 && (
                  <div className="text-[10px] text-green-700 font-bold">advance ₹{o.advance_paid} paid</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-text-secondary mb-3">
              {o.event_date && <span className="flex items-center gap-1"><Calendar size={12} />{o.event_date}</span>}
              {o.event_type && <span className="capitalize">{o.event_type}</span>}
              {o.city && <span className="flex items-center gap-1"><MapPin size={12} />{o.city}</span>}
              {o.guests > 0 && <span className="flex items-center gap-1"><Users size={12} />{o.guests} guests</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => handleAvailable(o)} disabled={busy === o.slot_id}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-none"
                data-testid={`vendor-inq-avail-${o.slot_id}`}>
                <Check size={14} className="mr-1" /> Available
              </Button>
              <Button onClick={() => handleUnavailable(o)} disabled={busy === o.slot_id}
                variant="outline" className="flex-1 border-red-300 text-red-700 hover:bg-red-50 rounded-none"
                data-testid={`vendor-inq-unavail-${o.slot_id}`}>
                <X size={14} className="mr-1" /> Not Available
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default VendorPendingInquiriesCard;
